/**
 * relativeTime — short human-readable relative-time strings for recent dates.
 *
 * Uses i18n keys so the output language matches the user's selected locale.
 * The `i18next` instance is imported directly (not via `useTranslation`) because
 * this is a plain utility function, not a React component or hook.
 *
 * Returns null for dates older than 7 days — callers should fall back to an
 * absolute date string (e.g. via `formatDate`).
 */
import i18n from '@/core/i18n';

/**
 * A server timestamp, read as the UTC it is.
 *
 * The API stores times with DateTime.UtcNow and serialises them without a zone
 * ("2026-09-11T19:31:07"), and `new Date()` reads a zoneless date-time as the
 * browser's *local* time. So every "ago" was off by the viewer's UTC offset: in
 * El Paso a request raised 77 minutes earlier read "2m ago", and newer ones read
 * as future and fell back to a bare date (reported 2026-09-11).
 *
 * Only zoneless date-times are touched. A string that already carries `Z` or an
 * offset is left alone, and a date-only string is already parsed as UTC by spec.
 * Deliberately not applied to absolute date formatting: fields such as a licence
 * expiry arrive as local midnight, and reading those as UTC would move them to
 * the previous day west of Greenwich.
 */
export function asUtcIfNaive(iso: string): string {
  return /T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(iso) ? `${iso}Z` : iso;
}

/**
 * Returns a short relative-time string for dates within the past 7 days.
 *
 * | Elapsed       | Example output (en-us) |
 * |---------------|------------------------|
 * | < 60 seconds  | "just now"             |
 * | < 60 minutes  | "5m ago"               |
 * | < 24 hours    | "3h ago"               |
 * | < 7 days      | "2d ago"               |
 * | >= 7 days     | null                   |
 *
 * @param iso ISO date/datetime string, null, or undefined.
 * @returns A translated relative-time string, or null when the date is too old
 *          (>= 7 days) or unparseable — callers should render an absolute date.
 */
export function relativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(asUtcIfNaive(iso));
  if (Number.isNaN(date.getTime())) return null;
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return null; // future dates — treat as absolute
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return i18n.t('time.justNow');
  const min = Math.floor(sec / 60);
  if (min < 60) return i18n.t('time.minutesAgo', { count: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return i18n.t('time.hoursAgo', { count: hr });
  const day = Math.floor(hr / 24);
  if (day < 7) return i18n.t('time.daysAgo', { count: day });
  return null;
}
